import React, { useState } from "react";
import { Flex, Text, Button, Form } from "@fluentui/react-northstar";
import Constants from "../../../../constants/text-constants";
import { changePassword } from "./controller";
import { observer } from "mobx-react-lite";
import { useMst } from "../../../../hooks/useMst";
import PropTypes from "prop-types";
import PasswordTextInput from "../../../atoms/PasswordTextInput";


const getErrorContent = (errorField) => {
  if (!errorField.show) return null;

  return  {
    error: true,
    content: errorField.error === "" ? undefined : errorField.error,
  }
};

const ChangePasswordForm = observer((props) => {
  const { changePasswordComplete, displayCancel, onCancel } = props;

  const initialErrorState = {
    currentPassword: {
      show: false,
      error: "",
    },
    newPassword: {
      show: false,
      error: "",
    },
    confirmNewPassword: {
      show: false,
      error: "",
    },
  };

  const { passwordHintStore } = useMst();

  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errors, setErrors] = useState(initialErrorState);

  const preValidate = () => {
    let error =
      state.newPassword !== state.confirmNewPassword
        ? Constants.PASSWORDS_MUST_MATCH
        : "";

    setErrors((prevState) => ({
      ...prevState, 
      confirmNewPassword: {
        show: error.length > 0,
        error,
      },
      newPassword: {
        show: error.length > 0,
        error: "",
      },
    }));

    return state.newPassword === state.confirmNewPassword;
  };

  const savePassword = () => {
    setErrors(initialErrorState);
    const isValid = preValidate();
    if (!isValid) return;

    setLoading(true);

    changePassword(
      state.currentPassword,
      state.newPassword,
      state.confirmNewPassword
    )
      .then((res) => {
        changePasswordComplete();
      })
      .catch((err) => {
        setLoading(false);

        if (!err.message) {
          console.warn("Error occured changing password -", err);
          return;
        }

        setErrors((prevState) => {
          let field =
            err.message === "Incorrect current password specified."
              ? "currentPassword"
              : "newPassword";
          let errorField = prevState[field];
          errorField.show = true;
          errorField.error = err.message;
          return { ...prevState, [field]: errorField };
        });
      });
  };

  const handleChangeField = (id, value) => {
    setState((prevState) => ({
      ...prevState,
      [id]: value,
    }));

    setErrors(initialErrorState);
  };

  const disableButton =
    state.currentPassword.length === 0 ||
    state.newPassword.length === 0 ||
    state.confirmNewPassword.length === 0 ||
    loading;

  const maxLength = passwordHintStore.passwordHints.get("maxLength");

  return (
    <Flex
      fill
      column
      hAlign="center"
      className="full-width tcm-form"
      space="between"
    >
      <Form className="change-password-form" data-testid="changePasswordForm">
        <PasswordTextInput
          id="currentPassword"
          fluid
          data-testid="currentPasswordField"
          label={Constants.CURRENT_PASSWORD}
          value={state.currentPassword}
          onChange={(e) => handleChangeField(e.target.id, e.target.value)}
          errorMessage={getErrorContent(errors.currentPassword)}
          disabled={loading}
          maxLength={maxLength}
        />
        <PasswordTextInput
          id="newPassword"
          fluid
          data-testid="newPasswordField"
          label={Constants.NEW_PASSWORD}
          value={state.newPassword}
          onChange={(e) => handleChangeField(e.target.id, e.target.value)}
          errorMessage={getErrorContent(errors.newPassword)}
          disabled={loading}
          maxLength={maxLength}
        />
        <PasswordTextInput
          id="confirmNewPassword"
          fluid
          data-testid="confirmPasswordField"
          label={Constants.CONFIRM_NEW_PASSWORD}
          value={state.confirmNewPassword}
          onChange={(e) => handleChangeField(e.target.id, e.target.value)}
          errorMessage={getErrorContent(errors.confirmNewPassword)}
          disabled={loading}
          maxLength={maxLength}
        />
        <Flex column className="password-hints">
          <Text>{passwordHintStore.passwordHintsText}</Text>
        </Flex>
        <Flex space="evenly">
          {displayCancel ? (
            <Button
              data-testid="changePasswordCancelBtn"
              secondary
              className="capitalize align-self-center"
              onClick={onCancel}
              content={Constants.CANCEL}
            />) : null}
          <Button
            primary
            loading={loading}
            className="capitalize align-self-center"
            data-testid="changePasswordBtn"
            onClick={savePassword}
            disabled={disableButton}
            content={Constants.CHANGE_PASSWORD}
          />
        </Flex>
      </Form>
    </Flex>
  );
});

ChangePasswordForm.propTypes = {
  changePasswordComplete: PropTypes.func,
};

export default ChangePasswordForm;
